
import React, { useState, useRef, useEffect, useContext } from 'react';
import ReactDOM from 'react-dom';
import { LayoutContext } from '../contexts/LayoutContext';
import { Card as CardTypeData, CardType, CardColor, PreparedCard } from '../types';
import { AttackIcon, DefenseIcon, HealIcon, DrawIcon, StealIcon, ShockIcon, EnergizeIcon, HibernationIcon, PushbackIcon, DispelIcon, DoubleCrossIcon, BodyUpgradeIcon, FortunesFavorIcon, PsyBlockIcon, PadlockIcon, DiseaseIcon, BrokenSecretIcon, CounterblastIcon, ChromabowIcon, CursedIronIcon, DiscerningEyeIcon, AleIcon, EnergySwordIcon, EyesOfMadnessIcon, FlyingScytheIcon, FrostIcon, InfernoGauntletIcon, InvincibleIcon, LarcendaggerIcon, LifeChargerIcon, LifeDrainIcon, MadnessControlIcon, NaturalGripIcon, PaladinsHammerIcon, PlantedBombIcon, ProtectorIcon, ShieldIcon, ManualRotateIcon, getCardDisplayName } from '../constants.tsx';
import { Language } from '../data/translations';
import './ManualRotation.css';
// Force rebuild v3 - Base64 Image Fix Applied
import { getEffectiveEssenceCostForPlayer } from '../services/gameLogic'
import { getColorFace, getAsset, getBestAssetUrl } from '../services/assets';
import MageIcon from '../assets/custom/Mage.png';
import OccultistIcon from '../assets/custom/Occultist.png';
import WarriorIcon from '../assets/custom/Warrior.png';

const PLACEHOLDER = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR4nGMAAQAABQABDQottAAAAABJRU5ErkJggg==';
const asset = (key: string) => {
  try {
    const globalFn = (globalThis as any).getAsset;
    if (typeof globalFn === 'function') return globalFn(key);
    if (typeof getBestAssetUrl === 'function') return getBestAssetUrl(key);
    if (typeof getAsset === 'function') return getAsset(key);
    return PLACEHOLDER;
  } catch {
    return PLACEHOLDER;
  }
};


// Reusable component to render one side of a card
const CardSide: React.FC<{ type: CardType; value: number; isActive: boolean, isActivating?: boolean, essenceCost?: number, displayEssenceCost?: number, subType?: string, faceSlug?: string, color?: CardColor, faceSide?: 'A' | 'B', language?: Language }> = ({ type, value, isActive, isActivating, essenceCost, displayEssenceCost, subType, faceSlug, color, faceSide = 'A', language = 'en' }) => {
  let bgColor, borderColor, textColor, Icon;
  const stashSlug = faceSlug || (subType ? String(subType).toLowerCase() : undefined)
  const faceUrl = essenceCost ? (stashSlug ? asset(`cards.stash.face.${stashSlug}.${faceSide === 'B' ? 'b' : 'a'}`) : undefined) : (color !== undefined ? getColorFace(color, faceSide) : undefined);

  switch (type) {
    case CardType.Attack:
      bgColor = 'bg-red-900/50'; borderColor = 'border-red-500'; textColor = 'text-red-400';
      Icon = <AttackIcon className="w-8 h-8 text-red-500" />; break;
    case CardType.Defense:
      bgColor = 'bg-blue-900/50'; borderColor = 'border-blue-500'; textColor = 'text-blue-400';
      Icon = <DefenseIcon className="w-8 h-8 text-blue-500" />; break;
    case CardType.Pushback:
      bgColor = 'bg-slate-800/50'; borderColor = 'border-slate-400'; textColor = 'text-slate-300';
      Icon = <PushbackIcon className="w-8 h-8 text-slate-400" />; break;
    case CardType.PsyBlock:
      bgColor = 'bg-slate-800/50'; borderColor = 'border-slate-400'; textColor = 'text-slate-300';
      Icon = <PsyBlockIcon className="w-8 h-8 text-slate-400" />; break;
    case CardType.Heal:
      bgColor = 'bg-green-900/50'; borderColor = 'border-green-500'; textColor = 'text-green-400';
      Icon = <HealIcon className="w-8 h-8 text-green-500" />; break;
    case CardType.Disease:
      bgColor = 'bg-lime-900/50'; borderColor = 'border-lime-500'; textColor = 'text-lime-400';
      Icon = <DiseaseIcon className="w-8 h-8 text-lime-500" />; break;
    case CardType.PsySearch:
      bgColor = 'bg-slate-800/50'; borderColor = 'border-slate-500'; textColor = 'text-slate-400';
      Icon = <DrawIcon className="w-8 h-8 text-slate-500" />; break;
    case CardType.Steal:
    case CardType.StealTower:
      bgColor = 'bg-purple-900/50'; borderColor = 'border-purple-500'; textColor = 'text-purple-400';
      Icon = <StealIcon className="w-8 h-8 text-purple-500" />; break;
    case CardType.DoubleCross:
      bgColor = 'bg-purple-900/50'; borderColor = 'border-purple-500'; textColor = 'text-purple-400';
      Icon = <DoubleCrossIcon className="w-8 h-8 text-purple-500" />; break;
    case CardType.Shock:
      bgColor = 'bg-yellow-900/50'; borderColor = 'border-yellow-500'; textColor = 'text-yellow-400';
      Icon = <ShockIcon className="w-8 h-8 text-yellow-500" />; break;
    case CardType.Energize:
    case CardType.ArcaneBoots:
      bgColor = 'bg-yellow-900/50'; borderColor = 'border-yellow-500'; textColor = 'text-yellow-400';
      Icon = <EnergizeIcon className="w-8 h-8 text-yellow-500" />; break;
    case CardType.Hibernation:
      bgColor = 'bg-pink-900/50'; borderColor = 'border-pink-500'; textColor = 'text-pink-400';
      Icon = <HibernationIcon className="w-8 h-8 text-pink-500" />; break;
    case CardType.Dispel:
      bgColor = 'bg-pink-900/50'; borderColor = 'border-pink-500'; textColor = 'text-pink-400';
      Icon = <DispelIcon className="w-8 h-8 text-pink-500" />; break;
    case CardType.BodyUpgrade:
      bgColor = 'bg-slate-900/70'; borderColor = 'border-slate-400'; textColor = 'text-pink-400';
      Icon = <BodyUpgradeIcon className="w-8 h-8 text-pink-500" />; break;
    case CardType.FortunesFavor:
      bgColor = 'bg-slate-900/70'; borderColor = 'border-slate-400'; textColor = 'text-yellow-400';
      Icon = <FortunesFavorIcon className="w-8 h-8 text-yellow-500" />; break;
    case CardType.CursedIron:
      bgColor = 'bg-slate-900/70'; borderColor = 'border-slate-400'; textColor = 'text-yellow-400';
      Icon = <CursedIronIcon className="w-8 h-8 text-yellow-500" />; break;
    case CardType.BrokenSecret:
      bgColor = 'bg-slate-900/70'; borderColor = 'border-slate-400'; textColor = 'text-cyan-400';
      Icon = <BrokenSecretIcon className="w-8 h-8 text-cyan-500" />; break;
    case CardType.Counterblast:
      bgColor = 'bg-slate-900/70'; borderColor = 'border-cyan-400'; textColor = 'text-cyan-300';
      Icon = <CounterblastIcon className="w-8 h-8 text-cyan-400" />; break;
    case CardType.PlantedBomb:
      bgColor = 'bg-slate-900/70'; borderColor = 'border-red-500'; textColor = 'text-red-400';
      Icon = <PlantedBombIcon className="w-8 h-8 text-red-500" />; break;
    case CardType.Chromabow:
      bgColor = 'bg-gray-800/50'; borderColor = 'border-amber-400'; textColor = 'text-amber-300';
      Icon = <ChromabowIcon className="w-8 h-8" />; break;
    case CardType.DiscerningEye:
      bgColor = 'bg-slate-900/70';
      borderColor = 'border-slate-400';
      textColor = 'text-purple-400';
      Icon = <DiscerningEyeIcon className="w-8 h-8 text-purple-500" />;
      break;
    case CardType.SealOfSilence:
      bgColor = 'bg-slate-900/70';
      borderColor = 'border-slate-400';
      textColor = 'text-cyan-300';
      Icon = <ShieldIcon className="w-8 h-8 text-cyan-400" />;
      break;
    case CardType.Ale:
      bgColor = 'bg-slate-900/70';
      borderColor = 'border-slate-400';
      textColor = 'text-yellow-400';
      Icon = <AleIcon className="w-8 h-8 text-yellow-500" />;
      break;
    case CardType.EnergySword:
      bgColor = 'bg-gray-800/50';
      borderColor = 'border-cyan-400';
      textColor = 'text-cyan-300';
      Icon = <EnergySwordIcon className="w-8 h-8 text-cyan-400" />;
      break;
    case CardType.EyesOfMadness:
      bgColor = 'bg-slate-900/70';
      borderColor = 'border-purple-500';
      textColor = 'text-purple-400';
      Icon = <EyesOfMadnessIcon className="w-8 h-8 text-purple-500" />;
      break;
    case CardType.MadnessControl:
      bgColor = 'bg-slate-900/70';
      borderColor = 'border-purple-500';
      textColor = 'text-purple-400';
      Icon = <MadnessControlIcon className="w-8 h-8 text-purple-500" />;
      break;
    case CardType.FlyingScythe:
      bgColor = 'bg-gray-800/50';
      borderColor = 'border-red-500';
      textColor = 'text-red-400';
      Icon = <FlyingScytheIcon className="w-8 h-8 text-red-500" />;
      break;
    case CardType.Frost:
      bgColor = 'bg-slate-900/70';
      borderColor = 'border-cyan-400';
      textColor = 'text-cyan-300';
      Icon = <FrostIcon className="w-8 h-8 text-cyan-400" />;
      break;
    case CardType.InfernoGauntlet:
      bgColor = 'bg-gray-800/50';
      borderColor = 'border-amber-400';
      textColor = 'text-red-400';
      Icon = <InfernoGauntletIcon className="w-8 h-8 text-red-500" />;
      break;
    case CardType.Invincible:
      bgColor = 'bg-slate-900/70';
      borderColor = 'border-yellow-300';
      textColor = 'text-yellow-200';
      Icon = <InvincibleIcon className="w-8 h-8 text-yellow-400" />;
      break;
    case CardType.Larcendagger:
      bgColor = 'bg-gray-800/50';
      borderColor = 'border-amber-400';
      textColor = 'text-purple-400';
      Icon = <LarcendaggerIcon className="w-8 h-8 text-purple-500" />;
      break;
    case CardType.LifeCharger:
      bgColor = 'bg-slate-900/70';
      borderColor = 'border-slate-400';
      textColor = 'text-green-400';
      Icon = <LifeChargerIcon className="w-8 h-8 text-green-500" />;
      break;
    case CardType.LifeDrain:
      bgColor = 'bg-slate-900/70';
      borderColor = 'border-slate-400';
      textColor = 'text-red-400';
      Icon = <LifeDrainIcon className="w-8 h-8 text-red-500" />;
      break;
    case CardType.NaturalGrip:
      bgColor = 'bg-slate-900/70';
      borderColor = 'border-slate-400';
      textColor = 'text-lime-400';
      Icon = <NaturalGripIcon className="w-8 h-8 text-lime-500" />;
      break;
    case CardType.Protector:
      bgColor = 'bg-slate-900/70';
      borderColor = 'border-cyan-400';
      textColor = 'text-cyan-300';
      Icon = <ProtectorIcon className="w-8 h-8 text-cyan-400" />;
      break;
    default:
      bgColor = 'bg-gray-900/50'; borderColor = 'border-gray-500'; textColor = 'text-gray-400'; Icon = null;
  }

  if (subType === 'Armory') {
    bgColor = 'bg-gray-800/50';
  } else if (subType === 'Artifact') {
    bgColor = 'bg-slate-900/70';
  }

  // Special styling for Paladin's Hammer
  if (type === CardType.Attack && subType === 'Armory' && essenceCost === 5 && value === 3) {
    bgColor = 'bg-gradient-to-br from-red-800/50 to-yellow-800/50'; // Holy hammer theme
    Icon = <PaladinsHammerIcon className="w-8 h-8 text-yellow-300" />;
  }

  return (
    <div className={`relative w-full h-full p-1 flex flex-col justify-between ${bgColor} transition-opacity duration-200 ${isActive ? 'opacity-100' : 'opacity-0'}`}>
      {faceUrl && <img src={faceUrl} alt="face" className="absolute inset-0 w-full h-full object-cover z-10 pointer-events-none" />}
      <div className="relative flex justify-between items-start">
        {/* Strength/Value circle hidden as requested */}
        <div className="hidden">
          {value}
        </div>
        {essenceCost && !isActivating && (
          <div className="absolute -top-[33px] -right-[35px] z-50 w-[65px] h-[65px] flex items-center justify-center">
            <div
              style={{ backgroundImage: `url(${asset('ui.cards.essenceCost')})` }}
              className="absolute inset-0 bg-contain bg-no-repeat bg-center drop-shadow-md z-0 pointer-events-none"
            />
            <span className="relative z-10 font-bold text-xl text-[#3E2723] leading-none pt-1 pr-[7px] tracking-tighter">{displayEssenceCost ?? essenceCost}</span>
          </div>
        )}
      </div>
      {essenceCost && subType && !isActivating && (
        <div className="absolute -bottom-[16px] -left-[16px] z-50 w-[33px] h-[33px] flex items-center justify-center">
          <div
            style={{
              backgroundImage: `url(${subType === 'Spell' ? MageIcon :
                subType === 'Artifact' ? OccultistIcon :
                  subType === 'Armory' ? WarriorIcon : ''
                })`
            }}
            className="absolute inset-0 bg-contain bg-no-repeat bg-center drop-shadow-md z-0 pointer-events-none"
          />
        </div>
      )}

      {/* Manual Rotation Icon for Two-Sided Cards */}

      <div className="flex justify-center items-center flex-grow z-0">
        {/* Type Icon removed as requested */}
      </div>
      <div className="h-4 flex items-end justify-center">
        {/* Card display name hidden as requested */}
      </div>
    </div>
  );
};

interface CardProps {
  card: CardTypeData;
  locked?: boolean;
  isHidden?: boolean;
  isInteractive?: boolean;
  isClickable?: boolean;
  isPlayable?: boolean;
  isTargetable?: boolean;
  source?: 'hand' | 'prepared' | 'prepared-reaction' | 'stash-choice';
  rotation?: number;
  rotationLocked?: boolean;
  allowManualRotation?: boolean;
  onRotationChange?: (cardId: string, newRotation: number) => void;
  onDropOnGroup?: (draggedGroupKey: string, targetGroupKey: string) => void;
  onDragOverCard?: (e: React.DragEvent<HTMLDivElement>) => void;
  onClick?: (cardId: string, rotation: number) => void;
  onDoubleClick?: (cardId: string, e: React.MouseEvent<HTMLDivElement>) => void;
  onMouseDown?: (e: React.MouseEvent<HTMLDivElement>) => void;
  onDragStartApp?: (card: CardTypeData, rotation: number, source: string) => void;
  onDragEndApp?: () => void;
  onMouseEnterApp?: (card: CardTypeData, rotation: number, source: string) => void;
  onMouseLeaveApp?: () => void;
  isGlowingForDayDiscard?: boolean;
  isGlowingForNightDiscard?: boolean;
  isGlowingForReaction?: boolean;
  count?: number;
  groupKey?: string;
  isActivating?: boolean;
  viewerIsOccultist?: boolean;
  onFlourishTrigger?: (cardId: string, rect: DOMRect, rotation: number, speed: number) => void;
  disableHoverScale?: boolean;
  language: Language;
}

const Card: React.FC<CardProps> = ({ card, locked = false, isHidden = false, isInteractive = false, isClickable = false, isPlayable = true, isTargetable = false, source, rotation = 0, rotationLocked = false, allowManualRotation, onRotationChange, onDropOnGroup, onDragOverCard, onClick, onDoubleClick, onMouseDown, onDragStartApp, onDragEndApp, onMouseEnterApp, onMouseLeaveApp, isGlowingForDayDiscard, isGlowingForNightDiscard, isGlowingForReaction, count = 1, groupKey, isActivating, viewerIsOccultist = false, onFlourishTrigger, disableHoverScale = false, language }) => {
  const [visualRotation, setVisualRotation] = useState(rotation);
  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isSnapLocked, setIsSnapLocked] = useState(false);
  const snapLockTimeoutRef = useRef<number | null>(null);

  // Rotation speed tracking for flourish animation (refs to avoid state updates)
  const rotationTimestampsRef = useRef<number[]>([]);
  const flourishCounterRef = useRef<{ cardId: string; count: number }>({ cardId: '', count: 0 });
  const manualClickTimestampsRef = useRef<number[]>([]); // New Ref for manual clicks
  const cardWrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setVisualRotation(rotation);
  }, [rotation]);

  // Cleanup snap lock timeout on unmount
  useEffect(() => {
    return () => {
      if (snapLockTimeoutRef.current) clearTimeout(snapLockTimeoutRef.current);
    };
  }, []);


  if (isHidden) {
    return (
      <div className="w-20 h-[7.7rem] rounded-lg border-2 border-slate-500 shadow-lg overflow-hidden">
        <img src={asset('cards.back')} alt="card back" className="w-full h-full object-cover" />
      </div>
    );
  }

  const isStashCard = !!card.essenceCost;
  const canManualRotate = allowManualRotation ?? isInteractive;

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    if (card && isInteractive && source && !locked) {
      const finalRotation = isStashCard ? 0 : Math.round(rotation / 180) * 180;
      e.dataTransfer.setData('cardId', card.id);
      e.dataTransfer.setData('rotation', finalRotation.toString());
      e.dataTransfer.setData('source', source);
      if (groupKey) e.dataTransfer.setData('groupKey', groupKey);

      // Explicitly set drag image using the card element
      const dragElement = e.currentTarget.querySelector('div') as HTMLElement;
      if (dragElement) {
        e.dataTransfer.setDragImage(dragElement, 40, 60);
      }

      if (onDragStartApp) {
        onDragStartApp(card, finalRotation, source);
      }
      setIsDragging(true);
    }
  };

  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    if (isStashCard || isActivating || !onRotationChange || rotationLocked) return;
    if (source !== 'hand' && source !== 'prepared' && source !== 'prepared-reaction') return;
    if (!canManualRotate) return;
    if (isSnapLocked) return; // Block rotation during cooldown after snap

    e.stopPropagation();

    const rotationAmount = Math.sign(e.deltaY) * -15;
    const newRotation = visualRotation + rotationAmount;

    setVisualRotation(newRotation);

    const currentBase = Math.round(rotation / 180) * 180;
    const delta = newRotation - currentBase;
    const absDelta = Math.abs(delta);
    if (absDelta >= 60) {
      const finalRotation = delta > 0 ? currentBase + 180 : currentBase - 180;
      onRotationChange(card.id, finalRotation);

      // Start snap cooldown to prevent over-rotation
      setIsSnapLocked(true);
      setVisualRotation(finalRotation);
      if (snapLockTimeoutRef.current) clearTimeout(snapLockTimeoutRef.current);
      snapLockTimeoutRef.current = window.setTimeout(() => {
        setIsSnapLocked(false);
      }, 300);
    } else {
      onRotationChange(card.id, newRotation);
    }

    // Track rotation speed for flourish animation
    if (onFlourishTrigger) {
      const now = Date.now();
      rotationTimestampsRef.current.push(now);
      // Keep only timestamps from last 500ms
      rotationTimestampsRef.current = rotationTimestampsRef.current.filter(t => now - t < 500);
      const stepsIn500ms = rotationTimestampsRef.current.length;

      if (stepsIn500ms >= 7) {
        // Fast rotation detected
        if (flourishCounterRef.current.cardId === card.id) {
          flourishCounterRef.current.count++;
        } else {
          flourishCounterRef.current = { cardId: card.id, count: 1 };
        }

        if (flourishCounterRef.current.count >= 3) {
          // Trigger flourish animation
          const rect = cardWrapperRef.current?.getBoundingClientRect();
          if (rect) {
            onFlourishTrigger(card.id, rect, newRotation, stepsIn500ms);
          }
          // Reset counters
          flourishCounterRef.current = { cardId: '', count: 0 };
          rotationTimestampsRef.current = [];
        }
      } else {
        // Speed not maintained - reset counter if different card
        if (flourishCounterRef.current.cardId !== card.id) {
          flourishCounterRef.current = { cardId: '', count: 0 };
        }
      }
    }
  };

  const handleMouseLeave = () => {
    if (canManualRotate && (source === 'hand' || source === 'prepared')) {
      setIsHovered(false);
      const finalRotation = isStashCard ? 0 : Math.round(rotation / 180) * 180;
      if (rotation !== finalRotation && onRotationChange) {
        onRotationChange(card.id, finalRotation);
      }
    } else {
      setIsHovered(false);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const draggedGroupKey = e.dataTransfer.getData('groupKey');
    if (draggedGroupKey && groupKey && draggedGroupKey !== groupKey && onDropOnGroup) {
      e.stopPropagation();
      onDropOnGroup(draggedGroupKey, groupKey);
    }
  };

  const handleClick = () => {
    console.log('[Card-DEBUG] handleClick called:', { cardType: card.type, isPlayable, hasOnClick: !!onClick, isInteractive, isClickable, passes: isPlayable && onClick && card && (isInteractive || isClickable) });
    if (isPlayable && onClick && card && (isInteractive || isClickable)) {
      const finalRotation = isStashCard ? 0 : Math.round(rotation / 180) * 180;

      onClick(card.id, finalRotation);
    }
  };

  const handleDoubleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (onDoubleClick && card && !locked) {
      onDoubleClick(card.id, e);
    }
  };

  const isTwoSided = card.typeB && card.valueB && !isStashCard;
  const normalizedRotation = (visualRotation % 360 + 360) % 360;
  const effectiveRotation = normalizedRotation > 180 ? 360 - normalizedRotation : normalizedRotation;
  const sideAPercent = 80 - (effectiveRotation / 180) * 60;
  const rotationFinished = normalizedRotation % 180 === 0;
  const overlayVisible = (isHovered || isDragging) && rotationFinished;
  const shouldTransmute = viewerIsOccultist && isStashCard && card.subType === 'Artifact';

  const currentType = (!isTwoSided || sideAPercent >= 50) ? card.type : card.typeB!;
  const currentValue = (!isTwoSided || sideAPercent >= 50) ? card.value : card.valueB!;
  const damageTypes: CardType[] = [CardType.Attack, CardType.EnergySword, CardType.FlyingScythe, CardType.InfernoGauntlet, CardType.Larcendagger, CardType.LifeDrain, CardType.Chromabow];
  const isShieldLike = (t: CardType, c: CardTypeData): boolean => {
    const baseShields: CardType[] = [
      CardType.Hibernation,
      CardType.Dispel,
      CardType.Pushback,
      CardType.Defense,
      CardType.PsyBlock,
      CardType.Frost,
      CardType.Invincible,
    ];
    const specialShields: CardType[] = [
      CardType.Protector,
      CardType.Counterblast,
      CardType.PlantedBomb,
      CardType.DiscerningEye,
      CardType.LifeCharger,
    ];
    if (baseShields.includes(t)) return true;
    if (specialShields.includes(t)) return true;
    if (c.extendedShield) return true;
    return false;
  };

  const getGlowColor = () => {
    if ((!isInteractive && !isClickable) || !card || isGlowingForDayDiscard) return '';
    if (isGlowingForReaction) return 'shadow-[0_0_20px_rgba(59,130,246,0.9)]';
    if (isStashCard) {
      if (card.subType === 'Armory') return 'hover:shadow-[0_0_22px_rgba(239,68,68,1)]';
      if (card.subType === 'Spell') return 'hover:shadow-[0_0_22px_rgba(236,72,153,1)]';
      if (card.subType === 'Artifact') return 'hover:shadow-[0_0_22px_rgba(0,0,0,0.95)]';
      return 'hover:shadow-[0_0_15px_rgba(251,191,36,0.8)]';
    }

    switch (card.color) {
      case CardColor.Red: return 'hover:shadow-[0_0_15px_rgba(239,68,68,0.8)]';
      case CardColor.Pink: return 'hover:shadow-[0_0_15px_rgba(236,72,153,0.8)]';
      case CardColor.Green: return 'hover:shadow-[0_0_15px_rgba(34,197,94,0.8)]';
      case CardColor.Black: return 'hover:shadow-[0_0_22px_rgba(0,0,0,0.95)]';
      case CardColor.Purple: return 'hover:shadow-[0_0_15px_rgba(168,85,247,0.8)]';
      case CardColor.Yellow: return 'hover:shadow-[0_0_15px_rgba(234,179,8,0.8)]';
      default: return '';
    }
  };

  const getBorderColor = (card: CardTypeData) => {
    if (card.subType === 'Artifact' && !isStashCard) return 'border-slate-400';
    if (card.type === CardType.EnergySword) return 'border-cyan-400';
    if (isStashCard) {
      if (card.subType === 'Armory') return 'border-red-500';
      if (card.subType === 'Spell') return 'border-pink-500';
      if (card.subType === 'Artifact') return 'border-black';
      return 'border-amber-400';
    }

    if (!isTwoSided) {
      switch (card.type) {
        case CardType.Heal: return 'border-green-500';
        case CardType.Disease: return 'border-lime-500';
        case CardType.PsySearch: return 'border-slate-500';
        case CardType.Steal: return 'border-purple-500';
        case CardType.StealTower: return 'border-purple-500';
        case CardType.DoubleCross: return 'border-purple-500';
        case CardType.Shock: return 'border-yellow-500';
        case CardType.Energize: return 'border-yellow-500';
        case CardType.Hibernation: return 'border-pink-500';
        case CardType.Dispel: return 'border-pink-500';
        default: return 'border-gray-500';
      }
    }
    switch (card.color) {
      case CardColor.Red: return 'border-red-500';
      case CardColor.Pink: return 'border-pink-500';
      case CardColor.Yellow: return 'border-yellow-500';
      case CardColor.Black: return 'border-black';
      case CardColor.Purple: return 'border-purple-500';
      case CardColor.Green: return 'border-green-500';
      default: return 'border-gray-500';
    }
  }

  const borderColor = getBorderColor(card);
  const glowColor = getGlowColor();
  const playabilityClass = isTargetable ? 'cursor-crosshair' : (isInteractive ? 'cursor-grab' : (isClickable || isGlowingForNightDiscard ? 'cursor-pointer' : ''));
  const dayDiscardGlow = isGlowingForDayDiscard ? 'shadow-[0_0_20px_rgba(255,255,255,0.9)]' : '';
  const nightDiscardWave = isGlowingForNightDiscard ? 'animate-[wave_2s_ease-in-out_infinite]' : '';
  const unplayableClass = !isPlayable && !isGlowingForNightDiscard ? 'grayscale cursor-not-allowed opacity-70' : '';

  const dynamicTranslate = () => {
    if (disableHoverScale) return '0px';
    if (isActivating) return '-20px';
    if (isHovered && (isInteractive || isClickable)) return '-8px';
    return '0px';
  };

  const getScale = () => {
    if (disableHoverScale) return 1;
    if (!isHovered || isActivating || isDragging) return 1;
    if (source === 'prepared' || source === 'prepared-reaction') return 1.1;
    return 1.2; // Default for hand and stash-choice
  };

  const currentScale = getScale();

  const hoverOffset = (isHovered && !isActivating && !isDragging) ? 24 : 0;

  return (
    <div
      id={card.id}
      draggable={isInteractive && !!card && isPlayable && !locked && !isTargetable}
      onDragStart={handleDragStart}
      onDragEnd={() => { setIsDragging(false); if (onDragEndApp) onDragEndApp(); }}
      onWheel={handleWheel}
      onMouseEnter={() => { setIsHovered(true); if (onMouseEnterApp) onMouseEnterApp(card, Math.round(rotation / 180) * 180, source || 'hand'); }}
      onMouseLeave={() => { handleMouseLeave(); if (onMouseLeaveApp) onMouseLeaveApp(); }}
      onDrop={handleDrop}
      onDragOver={onDragOverCard}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      onMouseDown={onMouseDown}
      ref={cardWrapperRef}
      className="relative group card-wheel-area"
      style={{
        width: `calc(5rem + ${count > 1 ? (Math.min(count, 5)) * 2 : 0}px)`,
        height: `calc(7.7rem + ${count > 1 ? (Math.min(count, 5)) * 2 : 0}px)`,
        zIndex: isActivating ? 20 : (isHovered && !isDragging ? 15 : 10),
      }}
    >
      {count > 1 && Array(Math.min(count, 5)).fill(0).map((_, i) => (
        <div
          key={i}
          className={`absolute w-20 h-[7.7rem] rounded-lg bg-slate-800 border-2 ${getBorderColor(card)}`}
          style={{
            top: i * 2,
            left: i * 2,
          }}
        />
      ))}
      <div
        className={`w-20 h-[7.7rem] rounded-lg border-2 shadow-xl backdrop-blur-sm transition-all duration-300 transform flex flex-col relative ${borderColor} ${glowColor} ${playabilityClass} ${dayDiscardGlow} ${nightDiscardWave} ${unplayableClass}`}
        style={{
          transform: `translateY(${dynamicTranslate()}) rotate(${visualRotation}deg) scale(${currentScale})`,
          position: 'absolute',
          top: count > 1 ? (Math.min(count, 5) - 1) * 2 : 0,
          left: count > 1 ? (Math.min(count, 5) - 1) * 2 : 0,
        }}
      >
        {isTwoSided ? (
          <>
            <div style={{ height: `${sideAPercent}%` }} className="transition-all duration-100 ease-linear overflow-hidden">
              <CardSide type={card.type} value={card.value} isActive={sideAPercent >= 50} isActivating={isActivating} color={card.color} faceSide={'A'} language={language} />
            </div>
            <div className="bg-slate-400/50 h-[2px] w-full flex-shrink-0" />
            <div style={{ height: `${100 - sideAPercent}%` }} className="transition-all duration-100 ease-linear overflow-hidden">
              <div className="w-full h-full transform rotate-180">
                <CardSide type={card.typeB!} value={card.valueB!} isActive={sideAPercent < 50} isActivating={isActivating} color={card.color} faceSide={'B'} language={language} />
              </div>
            </div>
          </>
        ) : (
          <div className="w-full h-full"><CardSide type={card.type} value={card.value} isActive={true} isActivating={isActivating} essenceCost={card.essenceCost} displayEssenceCost={card.essenceCost && viewerIsOccultist && card.subType === 'Artifact' ? Math.floor(card.essenceCost / 2) : card.essenceCost} subType={card.subType} faceSlug={(card as any).faceSlug} color={card.color} faceSide={'A'} language={language} /></div>
        )}


        {locked && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-10 pointer-events-none">
            <PadlockIcon className="w-10 h-10 text-yellow-400" />
          </div>
        )}
      </div>

      <div className={`absolute -top-6 left-1/2 -translate-x-1/2 px-2 py-1 rounded-md text-xs font-bold border ${shouldTransmute ? 'border-cyan-200' : 'border-slate-600'} flex items-center gap-1 transition-all duration-300`} style={{
        opacity: overlayVisible ? 1 : 0,
        transform: `translate(-50%, -${hoverOffset}px)`,
        background: shouldTransmute ? 'linear-gradient(90deg, rgba(22,210,238,0.9), rgba(255,255,255,0.9), rgba(22,210,238,0.9))' : 'rgba(0,0,0,0.7)',
        backgroundSize: shouldTransmute ? '200% 100%' : undefined,
        animation: shouldTransmute ? 'gradientShift 2s linear infinite' : undefined,
      }}>
        <span className="whitespace-nowrap" style={shouldTransmute ? {
          backgroundImage: 'linear-gradient(90deg, #000000, #1e3a8a, #000000)',
          backgroundSize: '200% 100%',
          WebkitBackgroundClip: 'text',
          backgroundClip: 'text',
          color: 'transparent',
          animation: 'gradientShift 2s linear infinite',
        } : { color: '#ffffff' }}>{getCardDisplayName(currentType, currentValue, card.subType, (card as any).faceSlug, language)}</span>
      </div>



      {isShieldLike(currentType, card) && (
        <img src={asset('ui.hud.shield')} alt="shield" className="w-8 h-8 absolute left-1/2 -translate-x-1/2 transition-all duration-300" style={{ bottom: '-0.9rem', opacity: overlayVisible ? 1 : 0, transform: `translate(-50%, ${hoverOffset}px)` }} />
      )}

            {/* Manual Rotation Icon for Two-Sided Cards - Refactored to Portal */}
      {isTwoSided && !isActivating && !locked && canManualRotate && (
        <RotationIconPortal
          cardId={card.id}
          rotation={rotation}
          onRotationChange={onRotationChange}
          onFlourishTrigger={onFlourishTrigger}
          cardWrapperRef={cardWrapperRef}
          isHovered={isHovered}
        />
      )}
      {count > 1 && (
        <div className="absolute -top-2 -left-3 w-8 h-8 rounded-full bg-slate-900 flex items-center justify-center font-bold text-white border-2 border-white z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          {count}
        </div>
      )}
    </div>
  );
};


// Portal component for the rotation icon to prevent z-fighting with chains
const RotationIconPortal: React.FC<{
  cardId: string;
  rotation: number;
  onRotationChange?: (cardId: string, newRotation: number) => void;
  onFlourishTrigger?: (cardId: string, rect: DOMRect, rotation: number, speed: number) => void;
  cardWrapperRef: React.RefObject<HTMLDivElement>;
  isHovered: boolean;
}> = ({ cardId, rotation, onRotationChange, onFlourishTrigger, cardWrapperRef, isHovered }) => {
  const portalRoot = document.getElementById('portal-root');
  const { isMobileShell } = useContext(LayoutContext);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const manualClickTimestampsRef = useRef<number[]>([]);

  useEffect(() => {
    let frameId: number;
    const updateRect = () => {
      if (cardWrapperRef.current) {
        const newRect = cardWrapperRef.current.getBoundingClientRect();
        setRect(prev => {
          if (!prev || Math.abs(prev.left - newRect.left) > 1 || Math.abs(prev.top - newRect.top) > 1 || prev.width !== newRect.width) {
            return newRect;
          }
          return prev;
        });
      }
      frameId = requestAnimationFrame(updateRect);
    };
    frameId = requestAnimationFrame(updateRect);
    return () => cancelAnimationFrame(frameId);
  }, [cardWrapperRef]);

  if (!portalRoot || !rect) return null;

  const scale = isMobileShell ? 1.1 : 1.0;
  
  return ReactDOM.createPortal(
    <div
      className="fixed z-50 flex items-center justify-center cursor-pointer transition-transform"
      style={{
        left: rect.left - 16,
        top: rect.bottom - 16,
        width: 33 * scale,
        height: 33 * scale,
        transform: 'translate(0, 0)',
        pointerEvents: 'auto'
      }}
      onPointerDown={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        e.stopPropagation();
        if (onRotationChange) {
          const finalRotation = Math.round(rotation / 180) * 180;
          onRotationChange(cardId, finalRotation + 180);

          if (onFlourishTrigger) {
            const now = Date.now();
            manualClickTimestampsRef.current.push(now);
            manualClickTimestampsRef.current = manualClickTimestampsRef.current.filter(t => now - t < 1500);

            if (manualClickTimestampsRef.current.length >= 5) {
              if (cardWrapperRef.current) {
                const cardRect = cardWrapperRef.current.getBoundingClientRect();
                onFlourishTrigger(cardId, cardRect, finalRotation + 180, 10);
              }
              manualClickTimestampsRef.current = [];
            }
          }
        }
      }}
    >
      <div className={`text-white drop-shadow-md portal-rotation-icon-container w-full h-full ${isHovered ? 'animate-rotate-hint' : ''}`}>
        <ManualRotateIcon className="w-full h-full" />
      </div>
    </div>,
    portalRoot
  );
};

export default Card;
